import React from "react";
import { useNavigate } from "react-router-dom";
import { useUserAuthContext } from "./useUserAuthContext";
import API_URL from "../api/API";

export default function Home() {
  const navigate = useNavigate();
  const { user, dispatch } = useUserAuthContext();
  const profile = user?.user;
  const course = user?.course || null;

  const [telegramBotLink, setTelegramBotLink] = React.useState("");
  const [telegramLoading, setTelegramLoading] = React.useState(true);

  // ✅ image fallback
  const [imgError, setImgError] = React.useState(false);

  const [workouts, setWorkouts] = React.useState([]);
const [workoutsLoading, setWorkoutsLoading] = React.useState(true);
const [selectedWorkoutKey, setSelectedWorkoutKey] = React.useState("");


  // ============================
  // Helpers
  // ============================
  const refreshUser = async () => {
    if (!user?.token) return;

    try {
      const res = await fetch(API_URL.GET_ME, {
        method: "GET",
        headers: { Authorization: `Bearer ${user.token}` },
      });

      const data = await res.json();

      if (res.ok && data.status) {
        const updatedAuth = {
          token: user.token,
          user: data.user,
          activeWorkoutMaster: data.activeWorkoutMaster || null,
          course: data.course || null,
        };
        localStorage.setItem("user", JSON.stringify(updatedAuth));
        window.location.reload();
      } else {
        alert("Failed to refresh status. Try again.");
      }
    } catch (err) {
      alert("Something went wrong. Try again.");
    }
  };

  // ============================
  // Telegram link loader
  // ============================
  React.useEffect(() => {
    const loadTelegramLink = async () => {
      try {
        setTelegramLoading(true);
        const res = await fetch(API_URL.GET_TELEGRAM_LINK, {
          method: "GET",
          headers: { Authorization: `Bearer ${user.token}` },
        });
        const data = await res.json();
        if (res.ok) setTelegramBotLink(data.telegramBotLink);
      } catch (err) {
        console.log("Telegram link fetch failed:", err);
      } finally {
        setTelegramLoading(false);
      }
    };

    if (user?.token) loadTelegramLink();
  }, [user]);

  // reset image error if picture changes
  React.useEffect(() => {
    setImgError(false);
  }, [profile?.picture]);

  React.useEffect(() => {
    const loadWorkouts = async () => {
      try {
        setWorkoutsLoading(true);
  
        const res = await fetch(API_URL.FRONTEND_GET_ALL_WORKOUTS, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        });
  
        const data = await res.json();
  
        if (res.ok && data.status) {
          setWorkouts(data.workouts || []);
        }
      } catch (err) {
        console.log("loadWorkouts error:", err);
      } finally {
        setWorkoutsLoading(false);
      }
    };
  
    if (user?.token) loadWorkouts();
  }, [user?.token]);
  

  if (!user || !user.user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 text-zinc-700">
        Loading...
      </div>
    );
  }



  const AMOUNT = 1499;
  const GPAY_NUMBER = "9890197282";
  const WHATSAPP_NUMBER = "9890197282";

  const selectedWorkout = workouts.find((w) => w.key === selectedWorkoutKey);

  // ============================
// Active Course Logic
// ============================
const activeWorkout = workouts.find(
  (w) => w.key === course?.workoutKey
);

const showActiveCourse =
  course && course.status === "active" && activeWorkout;

const showCompletedCourse =
  course && course.status === "completed" && activeWorkout;



  // WhatsApp message for admin verification
  const WHATSAPP_MESSAGE = encodeURIComponent(
    `I have completed payment for WorkoutBot.\n\n` +
      `Name: ${profile.name}\n` +
      `Email: ${profile.email}\n\n` +
      `Selected Course: ${
        selectedWorkout
          ? `${selectedWorkout.title} (${selectedWorkout.key})`
          : "NOT SELECTED"
      }\n\n` +
      `Please verify and activate my account.`
  );

  const canSendWhatsapp = Boolean(selectedWorkoutKey);

  

  const WHATSAPP_LINK = `https://wa.me/91${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`;

  const handleLogout = () => {
    localStorage.removeItem("user");
    dispatch({ type: "LOGOUT" });
    navigate("/");
  };

  const goToDashboard = () => navigate("/dashboard");


  const userInitial = profile?.name?.trim()?.[0]?.toUpperCase() || "U";

  return (
    <div className="min-h-screen bg-gray-50 text-zinc-900 py-10 px-4 sm:px-6 lg:px-8">
      {/* Top Bar */}
{/* Top Bar */}
<div className="mx-auto max-w-3xl flex items-center justify-between">
  <div>
    <div className="text-2xl font-bold text-indigo-700">SoulWorkout</div>
    <div className="text-xs text-zinc-500">Account setup & activation</div>
  </div>

  <div className="flex items-center gap-3">
  <button
    onClick={() => navigate("/dashboard")}
    className="rounded-xl border border-indigo-700 bg-white px-4 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50 transition"
  >
    Your Dashboard
  </button>

  <button
    onClick={handleLogout}
    className="rounded-xl border border-zinc-200 bg-white px-4 py-2 text-sm font-semibold text-zinc-800 hover:bg-zinc-50 transition"
  >
    Logout
  </button>

</div>
</div>
      {/* User Card */}
      <div className="mx-auto mt-8 max-w-3xl rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm flex items-center gap-4">
        <div className="h-14 w-14 overflow-hidden rounded-full border border-zinc-200 bg-zinc-100">
          {profile.picture && !imgError ? (
            <img
              src={profile.picture}
              alt="Profile"
              className="h-full w-full object-cover"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-sm font-semibold text-zinc-600">
              {userInitial}
            </div>
          )}
        </div>

        <div className="flex-1">
          <div className="text-base font-semibold text-zinc-900">
            {profile.name}
          </div>
          <div className="text-sm text-zinc-600">{profile.email}</div>
        </div>
      </div>

      {/* Status Card */}
      <div className="mx-auto mt-6 max-w-3xl rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm font-semibold text-zinc-900">
            Your current status
          </div>
          <button
            onClick={refreshUser}
            className="text-xs text-zinc-500 hover:underline"
          >
            Refresh
          </button>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-sm text-zinc-700">Telegram connected</div>
            <span
              className={`rounded-full px-3 py-1 text-xs font-semibold ${
                profile.telegramLinked
                  ? "bg-green-100 text-green-800"
                  : "bg-zinc-200 text-zinc-800"
              }`}
            >
              {profile.telegramLinked ? "Connected" : "Not connected"}
            </span>
          </div>

           {/* Subscription status */}
          <div className="flex items-center justify-between">
            <div className="text-sm text-zinc-700">Subscription</div>

            {profile.subscriptionEnd ? (
              new Date(profile.subscriptionEnd) > new Date() ? (
                <span className="rounded-full px-3 py-1 text-xs font-semibold bg-green-100 text-green-800">
                  Active
                </span>
              ) : (
                <span className="rounded-full px-3 py-1 text-xs font-semibold bg-red-100 text-red-800">
                  Expired
                </span>
              )
            ) : (
              <span className="rounded-full px-3 py-1 text-xs font-semibold bg-zinc-200 text-zinc-800">
                Not active
              </span>
            )}
          </div>

          {/* Valid till */}
          <div className="flex items-center justify-between">
            <div className="text-sm text-zinc-700">Valid till</div>

            <span className="text-sm font-semibold text-zinc-900">
              {profile.subscriptionEnd
                ? new Date(profile.subscriptionEnd).toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })
                : "-"}
            </span>
          </div>


          <div className="flex items-center justify-between">
            <div className="text-sm text-zinc-700">Account access</div>
            <span
              className={`rounded-full px-3 py-1 text-xs font-semibold ${
                profile.isActive
                  ? "bg-green-100 text-green-800"
                  : "bg-zinc-200 text-zinc-800"
              }`}
            >
              {profile.isActive ? "Active" : "Not active"}
            </span>
          </div>
        </div>

        <div className="mt-4 text-sm text-zinc-600">
          Workouts are sent only after your payment is done and the account is activated.
        </div>
      </div>

      {/* Active Course (if exists) */}
      {showActiveCourse && (
  <div className="mx-auto mt-6 max-w-3xl rounded-2xl border border-green-200 bg-green-50 p-6 shadow-sm">
    <div className="flex items-center justify-between">
      <div>
        <div className="text-sm font-semibold text-green-900">
          Your Active Course
        </div>
        <div className="mt-1 text-lg font-bold text-green-800">
          {activeWorkout.title}
        </div>
      </div>

      <span className="rounded-full bg-green-600 px-3 py-1 text-xs font-semibold text-white">
        Active
      </span>
    </div>

    {activeWorkout.description && (
      <div className="mt-3 text-sm text-green-900 leading-relaxed">
        {activeWorkout.description}
      </div>
    )}

    <div className="mt-4 rounded-xl bg-white border border-green-200 p-4 text-sm text-zinc-700">
      <div>
        Current Step:{" "}
        <span className="font-semibold">
          {course.currentStepIndex}
        </span>
      </div>

      <div className="mt-1">
        Steps Tracked:{" "}
        <span className="font-semibold">
          {course.totalTrackedSteps}
        </span>
      </div>
    </div>
  </div>
)}

{showCompletedCourse && (
  <div className="mx-auto mt-6 max-w-3xl rounded-2xl border border-indigo-200 bg-indigo-50 p-6 shadow-sm">
    <div className="text-lg font-bold text-indigo-900">
      🎉 Course Completed!
    </div>

    <div className="mt-2 text-sm text-indigo-800">
      You have successfully completed{" "}
      <span className="font-semibold">{activeWorkout.title}</span>.
    </div>

    <div className="mt-3 text-sm text-indigo-700">
      Completed on{" "}
      {course.completedAt
        ? new Date(course.completedAt).toLocaleDateString("en-IN")
        : "-"}
    </div>

    <div className="mt-4 text-sm text-indigo-900 font-semibold">
      Start a new course to continue your journey.
    </div>
  </div>
)}

      {/* Steps */}
      <div className="mx-auto mt-6 max-w-3xl space-y-6">
        {/* Step 1 - Telegram */}
        <div className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm hover:shadow-md transition">
          <div className="text-sm font-semibold text-zinc-900">
            Step 1 — Connect Telegram
          </div>
          <p className="mt-2 text-sm text-zinc-600">
            Your spiritual workouts are delivered privately through our
            Telegram bot. Once connected, you’ll receive your workouts.
          </p>

          <a
            href={telegramBotLink || "#"}
            target="_blank"
            rel="noreferrer"
            className={`mt-4 block w-full rounded-2xl px-6 py-3 text-center text-sm font-semibold text-white ${
              telegramLoading || !telegramBotLink
                ? "bg-zinc-300 cursor-not-allowed"
                : "bg-indigo-700 hover:bg-indigo-600 transition"
            }`}
            onClick={(e) => {
              if (telegramLoading || !telegramBotLink) e.preventDefault();
            }}
          >
            {telegramLoading ? "Loading Telegram link..." : "Open Telegram Bot"}
          </a>

          <div className="mt-2 text-xs text-zinc-500">
            On Telegram, press <span className="font-semibold">Start</span> to
            connect.
          </div>
        </div>

        {/* Step 2 - Payment */}
       {/* Step 2 - Payment */}
<div className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm">
  <div className="text-sm font-semibold text-zinc-900">
    Step 2 — Complete Payment
  </div>

  <p className="mt-2 text-sm text-zinc-600 leading-relaxed">
  Make a **one-time annual payment** to access your course for 12 months. 
  After payment, select your course and send the details on WhatsApp for verification. 
  Workouts will be delivered on telegram as per schedule.
</p>

  {/* Amount */}
  <div className="mt-4 rounded-2xl border border-zinc-200 bg-zinc-50 p-4">
    <div className="text-sm text-zinc-700">Amount</div>
    <div className="mt-1 text-xl font-bold text-zinc-900">
      ₹{AMOUNT} / year
    </div>
    <div className="mt-1 text-xs text-zinc-500">
      Less than ₹5/day
    </div>
  </div>

  {/* UPI */}
  <div className="mt-4 rounded-2xl border border-zinc-200 bg-white p-4">
    <div className="text-sm font-semibold text-zinc-900">
      Pay via Google Pay / UPI
    </div>

    <div className="mt-2 text-sm text-zinc-700">
      UPI Number:{" "}
      <span className="font-semibold text-zinc-900">{GPAY_NUMBER}</span>
    </div>

    <div className="mt-3 flex items-center justify-center rounded-2xl border border-dashed border-zinc-300 bg-zinc-50 p-6 text-xs text-zinc-500">
      QR will be updated here
    </div>
  </div>

  {/* Course Select */}
  <div className="mt-5">
    <div className="text-sm font-semibold text-zinc-900">
      Select your course
    </div>

    <p className="mt-1 text-sm text-zinc-600">
      This will be included in your WhatsApp message.
    </p>

    {workoutsLoading ? (
      <div className="mt-3 text-sm text-zinc-500">Loading courses...</div>
    ) : workouts.length === 0 ? (
      <div className="mt-3 text-sm text-red-600">
        No courses available right now.
      </div>
    ) : (
      <select
        value={selectedWorkoutKey}
        onChange={(e) => setSelectedWorkoutKey(e.target.value)}
        className="mt-3 w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-indigo-600"
      >
        <option value="">-- Select a course --</option>
        {workouts.map((w) => (
          <option key={w.key} value={w.key}>
            {w.title}
          </option>
        ))}
      </select>
    )}

    {selectedWorkout && (
      <div className="mt-3 rounded-2xl border border-indigo-200 bg-indigo-50 p-4">
        <div className="text-sm font-semibold text-indigo-900">
          {selectedWorkout.title}
        </div>
        <div className="mt-1 text-xs text-indigo-700 leading-relaxed">
          {selectedWorkout.description}
        </div>
      </div>
    )}
  </div>

  {/* WhatsApp */}
  <div className="mt-5">
    <a
      href={canSendWhatsapp ? WHATSAPP_LINK : "#"}
      target="_blank"
      rel="noreferrer"
      className={`block w-full rounded-2xl px-6 py-4 text-center text-sm font-semibold text-white transition ${
        canSendWhatsapp
          ? "bg-green-600 hover:bg-green-700"
          : "bg-zinc-300 cursor-not-allowed"
      }`}
      onClick={(e) => {
        if (!canSendWhatsapp) e.preventDefault();
      }}
    >
      ✅ Payment done? Message us on WhatsApp
    </a>

    {!canSendWhatsapp && (
      <div className="mt-3 text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-2xl px-4 py-3">
        Please select a course before sending WhatsApp message.
      </div>
    )}

      <div className="mt-3 text-xs text-zinc-500">
        After sending the WhatsApp message, we’ll verify your payment and activate your account. 
        If your subscription is already active and you are starting a new course, we’ll update your access accordingly.
      </div>
  </div>
</div>


        {/* Step 3 - Activation */}
        <div className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm hover:shadow-md transition">
          <div className="text-sm font-semibold text-zinc-900">
            Step 3 — Activation
          </div>

          <p className="mt-2 text-sm text-zinc-600">
            After verification, your account will be activated and your workouts
            will begin.
          </p>

          <div className="mt-4 rounded-2xl border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-600">
            <div className="font-semibold text-zinc-900">Note</div>
            <div className="mt-1 leading-relaxed text-zinc-600">
              Verification may take a little time depending on availability.
              You’ll automatically start receiving workouts once activated.
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mx-auto max-w-3xl mt-10 border-t border-zinc-200 pt-6 text-center text-xs text-zinc-500">
        © {new Date().getFullYear()} SoulWorkout • Built for daily reform
      </div>
    </div>
  );
}
